import {
  Box,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Typography,
} from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { SavedBrandContext } from "contexts/SavedBrand";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LIGHT_GREY } from "styles/oneOffColor";

import { isPage } from "utils/general";
import { trackEvent } from "utils/tracking";
import { useCollection } from "utils/useCollection";

import { Page, PageMenu } from "../schema";
import LeftDrawerWrapper from "./Wrapper";
import styles from "./styles";

interface Props {
  open: boolean;
  handleClose: () => void;
  handleReportBug: () => void;
  handleLogOut: () => void;
  openSettingsMenuOnly?: boolean;
  anchorEl?: HTMLElement | null;
}

const TopNavigationLeftDrawer = ({
  open,
  handleClose,
  handleReportBug,
  handleLogOut,
  openSettingsMenuOnly,
  anchorEl,
}: Props) => {
  const navigate = useNavigate();
  const { currentUser, currentOrg } = useContext(OrganizationUserContext);
  const { userIntegration } = useContext(UserIntegrationsContext);
  const { setOpenSavedBrandsOnMobile } = useContext(SavedBrandContext);
  const { getTotal } = useCollection();
  const total = getTotal();

  const [openSubmenu, setOpenSubmenu] = useState<boolean>(false);

  const navigateRoute = (page: string) => {
    trackEvent("Page Navigated", { route: `/${PageMenu[page].routes}` });
    navigate(`/${PageMenu[page].routes}`);
    handleClose();
  };

  const openSavedBrands = () => {
    setOpenSavedBrandsOnMobile(true);
  };

  const renderListItem = (page: string) => {
    const isActive = isPage(PageMenu[page].routes);

    return (
      <ListItem
        key={PageMenu[page].label}
        disablePadding
        sx={[isActive ? { backgroundColor: LIGHT_GREY } : {}]}
      >
        <ListItemButton onClick={() => navigateRoute(page)}>
          <Box component={"i"} className={PageMenu[page].icon} sx={{ mr: 1 }} />{" "}
          {PageMenu[page].label}
          {PageMenu[page].label === "Brands" && (
            <>
              {total > 0 && <Box sx={styles.savedBrandIcon}>{total} saved</Box>}
              {total > 0 && (
                <IconButton
                  sx={{ fontSize: 12 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenSubmenu((prev) => !prev);
                    trackEvent("Saved Brands Submenu Toggle");
                  }}
                >
                  <Box
                    component="i"
                    className={`fa-regular fa-chevron-${openSubmenu ? "up" : "down"}`}
                  />
                </IconButton>
              )}
            </>
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  const renderAskQuestion = () => {
    if (!currentOrg?.isPaidUser) {
      return <></>;
    }
    return (
      <ListItem disablePadding>
        <ListItemButton onClick={handleReportBug}>
          <Box
            component={"i"}
            className={"fa-thin fa-circle-question"}
            sx={{ mr: 1 }}
          />{" "}
          Ask A Question
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <LeftDrawerWrapper
      open={open}
      handleClose={handleClose}
      openSettingsMenuOnly={openSettingsMenuOnly}
      anchorEl={anchorEl}
    >
      <List sx={{ width: 250 }}>
        <Box sx={styles.name}>
          <strong>{currentUser?.name}</strong> ({currentUser?.email})
        </Box>

        {userIntegration?.email &&
          userIntegration?.email !== currentUser?.email && (
            <Box sx={styles.email}>
              Emails are sent from <strong>{userIntegration?.email}</strong>
            </Box>
          )}

        {openSettingsMenuOnly ? null : (
          <>
            <Divider sx={{ my: 1 }} />
            {renderListItem(Page.HOME)}
            {renderListItem(Page.VA)}
            {renderAskQuestion()}

            <Box sx={{ mt: 2 }}>
              <Typography
                gutterBottom
                sx={{ pl: 2, color: "purple.main" }}
                variant="caption"
              >
                Outreach Tools
              </Typography>

              {renderListItem(Page.BRANDS)}

              <Collapse in={openSubmenu} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItemButton sx={{ pl: 4 }} onClick={openSavedBrands}>
                    <Box
                      component={"i"}
                      className={"fa-thin fa-bookmark"}
                      sx={{ mr: 1 }}
                    />
                    View Saved Brands
                  </ListItemButton>
                </List>
              </Collapse>
              {renderListItem(Page.RECOMMENDATIONS)}
              {renderListItem(Page.INBOX)}
              {renderListItem(Page.CAMPAIGNS)}
              {renderListItem(Page.LOCAL)}
            </Box>
          </>
        )}

        {openSettingsMenuOnly ? (
          <Box>
            <Divider sx={{ my: 2 }} />
          </Box>
        ) : (
          <></>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography
            gutterBottom
            sx={{ pl: 2, color: "purple.main" }}
            variant="caption"
            color="textSecondary"
          >
            Account & Settings
          </Typography>
          {openSettingsMenuOnly ? renderListItem(Page.VA) : null}
          {renderListItem(Page.SETTINGS)}
          {renderListItem(Page.TEMPLATES)}
          {renderListItem(Page.PROFILE)}

          {openSettingsMenuOnly ? renderAskQuestion() : null}

          <ListItem disablePadding>
            <ListItemButton onClick={handleLogOut}>
              <Box
                component={"i"}
                className={"fa-thin fa-sign-out"}
                sx={{ mr: 1 }}
              />{" "}
              Log Out
            </ListItemButton>
          </ListItem>
        </Box>
      </List>
    </LeftDrawerWrapper>
  );
};

export default TopNavigationLeftDrawer;
